import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';
import ConversationLanes from '../../components/ConversationLanes/ConversationLanes';
import DashboardHeader from '../../components/DashboardHeader/DashboardHeader';
import StatsCards from '../../components/StatsCards/StatsCards';
import ConfigurationModal from '../../components/ConfigurationModal/ConfigurationModal';
import { FaSync, FaCog, FaUserCog, FaVolumeUp, FaVolumeMute } from 'react-icons/fa';
import {
    playNotificationSound,
    isNotificationSoundEnabled,
    setNotificationSoundEnabled,
    initializeNotifications
} from '../../utils/notificationSound';
import './Dashboard.scss';

const PAGE_SIZE = 20;
const LANE_IDS = ['pending', 'confirmed', 'needs_reschedule'];

const createInitialLaneState = () => ({
    pending: { conversations: [], page: 1, totalPages: 1, totalCount: 0, limit: PAGE_SIZE, loading: false },
    confirmed: { conversations: [], page: 1, totalPages: 1, totalCount: 0, limit: PAGE_SIZE, loading: false },
    needs_reschedule: { conversations: [], page: 1, totalPages: 1, totalCount: 0, limit: PAGE_SIZE, loading: false }
});

const Dashboard = () => {
    const { technician, client, isAdmin, isManager, getDashboardStats, apiRequest } = useAuth();
    const [stats, setStats] = useState(null);
    const [laneData, setLaneData] = useState(createInitialLaneState);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [refreshing, setRefreshing] = useState(false);
    const [showTestModal, setShowTestModal] = useState(false);
    const [soundEnabled, setSoundEnabled] = useState(isNotificationSoundEnabled());
    const [creatingReminderFlowConversationId, setCreatingReminderFlowConversationId] = useState(null);
    const [activeFilters, setActiveFilters] = useState({
        status: 'all',
        technician: 'all',
        dateRange: '7d',
        search: ''
    });

    const previousConversationIds = useRef(new Set());
    const isInitialLoad = useRef(true);
    const hasCompletedInitialDashboardLoad = useRef(false);
    const laneDataRef = useRef(createInitialLaneState());

    useEffect(() => {
        laneDataRef.current = laneData;
    }, [laneData]);

    const getVisibleLaneIds = useCallback((statusFilter = activeFilters.status) => {
        return statusFilter === 'all' ? LANE_IDS : [statusFilter];
    }, [activeFilters.status]);

    const buildConversationParams = useCallback((laneId, pageNumber) => {
        const params = new URLSearchParams(window.location.search);
        params.set('page', String(pageNumber));
        params.set('limit', String(PAGE_SIZE));
        params.set('status', laneId);

        if (activeFilters.technician !== 'all') {
            params.set('technicianId', activeFilters.technician);
        }

        if (activeFilters.dateRange) {
            params.set('dateRange', activeFilters.dateRange);
        }

        if (activeFilters.search.trim()) {
            params.set('search', activeFilters.search.trim());
        }

        return params;
    }, [activeFilters]);

    const fetchLanePage = useCallback(async (laneId, pageNumber) => {
        const params = buildConversationParams(laneId, pageNumber);
        const result = await apiRequest(`/api/ejento/conversations?${params.toString()}`);

        if (!result.success) {
            throw new Error(result.message || `Failed to load ${laneId} conversations`);
        }

        return {
            conversations: result.data.conversations || [],
            page: result.data.page || pageNumber,
            totalPages: Math.max(result.data.totalPages || 1, 1),
            totalCount: result.data.totalCount || 0,
            limit: PAGE_SIZE,
            loading: false
        };
    }, [apiRequest, buildConversationParams]);

    const applyNotificationCheck = useCallback((laneResponses) => {
        const combinedConversations = laneResponses.flatMap(({ data }) => data.conversations || []);

        if (!isInitialLoad.current && combinedConversations.length > 0) {
            const newAppointments = combinedConversations.filter(
                conversation => !previousConversationIds.current.has(conversation.id)
            );

            if (newAppointments.length > 0) {
                console.log(`🔔 ${newAppointments.length} new appointment(s) detected!`);
                playNotificationSound();
            }
        }

        previousConversationIds.current = new Set(combinedConversations.map(conversation => conversation.id));
        isInitialLoad.current = false;
    }, []);

    const loadAllLaneData = useCallback(async (showLoadingSpinner = false, pageOverrides = {}, options = {}) => {
        try {
            const { reloadStats = true } = options;

            if (showLoadingSpinner) {
                setLoading(true);
            }

            setError('');

            if (reloadStats) {
                const statsResult = await getDashboardStats();
                if (statsResult.success) {
                    setStats(statsResult.data);
                }
            }

            const visibleLaneIds = getVisibleLaneIds();
            const laneResponses = await Promise.all(
                visibleLaneIds.map(async (laneId) => ({
                    laneId,
                    data: await fetchLanePage(
                        laneId,
                        pageOverrides[laneId] || laneDataRef.current[laneId]?.page || 1
                    )
                }))
            );

            setLaneData(prev => {
                const next = { ...prev };

                laneResponses.forEach(({ laneId, data }) => {
                    next[laneId] = data;
                });

                return next;
            });

            applyNotificationCheck(laneResponses);
        } catch (err) {
            console.error('Error loading dashboard data:', err);
            setError('Failed to load dashboard data');
        } finally {
            setLoading(false);
        }
    }, [applyNotificationCheck, fetchLanePage, getDashboardStats, getVisibleLaneIds]);

    useEffect(() => {
        const pageOverrides = {};
        getVisibleLaneIds().forEach((laneId) => {
            pageOverrides[laneId] = 1;
        });

        previousConversationIds.current = new Set();
        isInitialLoad.current = true;

        setLaneData(prev => {
            const next = { ...prev };
            LANE_IDS.forEach((laneId) => {
                next[laneId] = {
                    ...next[laneId],
                    page: 1
                };
            });
            return next;
        });

        if (!hasCompletedInitialDashboardLoad.current) {
            hasCompletedInitialDashboardLoad.current = true;
            loadAllLaneData(true, pageOverrides, { reloadStats: true });
            return;
        }

        setLaneData(prev => {
            const next = { ...prev };
            getVisibleLaneIds().forEach((laneId) => {
                next[laneId] = {
                    ...next[laneId],
                    loading: true
                };
            });
            return next;
        });

        loadAllLaneData(false, pageOverrides, { reloadStats: false });
    }, [activeFilters.status, activeFilters.technician, activeFilters.dateRange, activeFilters.search, getVisibleLaneIds, loadAllLaneData]);

    useEffect(() => {
        const pollInterval = setInterval(() => {
            loadAllLaneData(false);
        }, 10000);

        const handleFocus = () => {
            loadAllLaneData(false);
        };

        window.addEventListener('focus', handleFocus);

        return () => {
            clearInterval(pollInterval);
            window.removeEventListener('focus', handleFocus);
        };
    }, [loadAllLaneData]);

    const handleRefresh = async () => {
        setRefreshing(true);
        try {
            console.log('🔄 Triggering appointment sync...');
            await apiRequest(
                `/api/ejento/appointments/sync${window.location.search}`,
                { method: 'POST' }
            );
            console.log('✅ Appointment sync completed');
        } catch (err) {
            console.warn('⚠️ Appointment sync failed:', err);
        }

        await loadAllLaneData(false);
        setRefreshing(false);
    };

    const handleFilterChange = (newFilters) => {
        setActiveFilters(prev => ({ ...prev, ...newFilters }));
    };

    const handleSoundToggle = () => {
        const newValue = !soundEnabled;
        setSoundEnabled(newValue);
        setNotificationSoundEnabled(newValue);

        if (newValue) {
            initializeNotifications();
            playNotificationSound();
        }
    };

    const handleCreateReminderFlow = useCallback(async (conversationId) => {
        try {
            setCreatingReminderFlowConversationId(conversationId);

            const response = await apiRequest(
                `/api/ejento/conversations/${conversationId}/create-reminder-flow${window.location.search}`,
                {
                    method: 'POST'
                }
            );

            await loadAllLaneData(false);
            return response;
        } finally {
            setCreatingReminderFlowConversationId(null);
        }
    }, [apiRequest, loadAllLaneData]);

    const handleConversationUpdate = useCallback(async (conversationId, updates) => {
        try {
            console.log('🔄 Updating conversation:', conversationId, 'with:', updates);

            const allConversations = Object.values(laneDataRef.current).flatMap(lane => lane.conversations);
            const conversation = allConversations.find(conv => conv.id === conversationId);
            const oldStatus = conversation?.status;

            setLaneData(prev => {
                const next = { ...prev };
                LANE_IDS.forEach((laneId) => {
                    next[laneId] = {
                        ...next[laneId],
                        conversations: next[laneId].conversations.map(conv =>
                            conv.id === conversationId ? { ...conv, ...updates } : conv
                        )
                    };
                });
                return next;
            });

            if (updates.status && oldStatus && updates.status !== oldStatus) {
                setStats(prev => {
                    if (!prev || !prev.conversations) {
                        return prev;
                    }

                    const nextStats = { ...prev, conversations: { ...prev.conversations } };

                    if (nextStats.conversations[oldStatus] !== undefined) {
                        nextStats.conversations[oldStatus] = Math.max(0, nextStats.conversations[oldStatus] - 1);
                    }

                    nextStats.conversations[updates.status] = (nextStats.conversations[updates.status] || 0) + 1;
                    return nextStats;
                });
            }

            const response = await apiRequest(
                `/api/ejento/conversations/${conversationId}${window.location.search}`,
                {
                    method: 'PATCH',
                    body: JSON.stringify(updates)
                }
            );

            if (!response.success) {
                await loadAllLaneData(false);
                return;
            }

            await loadAllLaneData(false);
        } catch (error) {
            console.error('Error updating conversation:', error);
            await loadAllLaneData(false);
        }
    }, [apiRequest, loadAllLaneData]);

    const handleLanePageChange = useCallback(async (laneId, nextPage) => {
        const lane = laneDataRef.current[laneId];
        if (!lane || nextPage < 1 || nextPage > lane.totalPages || nextPage === lane.page) {
            return;
        }

        setLaneData(prev => ({
            ...prev,
            [laneId]: {
                ...prev[laneId],
                loading: true
            }
        }));

        try {
            const lanePageData = await fetchLanePage(laneId, nextPage);

            setLaneData(prev => ({
                ...prev,
                [laneId]: lanePageData
            }));
        } catch (err) {
            console.error(`Error loading ${laneId} page ${nextPage}:`, err);
            setLaneData(prev => ({
                ...prev,
                [laneId]: {
                    ...prev[laneId],
                    loading: false
                }
            }));
        }
    }, [fetchLanePage]);

    if (loading) {
        return (
            <div className="dashboard dashboard--loading">
                <LoadingSpinner size="large" />
                <p>Loading dashboard...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="dashboard dashboard--error">
                <div className="error-content">
                    <h2>Error Loading Dashboard</h2>
                    <p>{error}</p>
                    <button className="btn btn--primary" onClick={() => loadAllLaneData(true)}>
                        Try Again
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="dashboard">
            <DashboardHeader
                technician={technician}
                client={client}
                isAdmin={isAdmin}
                isManager={isManager}
                onRefresh={handleRefresh}
                refreshing={refreshing}
            />

            <div className="dashboard__content">
                <StatsCards stats={stats} />

                <div className="dashboard__main">
                    <div className="dashboard__controls">
                        <div className="filters">
                            <input
                                type="text"
                                value={activeFilters.search}
                                onChange={(e) => handleFilterChange({ search: e.target.value })}
                                className="filter-search"
                                placeholder="Search appointment, customer, tech..."
                            />

                            <select
                                value={activeFilters.status}
                                onChange={(e) => handleFilterChange({ status: e.target.value })}
                                className="filter-select"
                            >
                                <option value="all">All Statuses</option>
                                <option value="pending">Pending</option>
                                <option value="confirmed">Confirmed</option>
                                <option value="needs_reschedule">Needs Reschedule</option>
                            </select>

                            {(isAdmin || isManager) && (
                                <select
                                    value={activeFilters.technician}
                                    onChange={(e) => handleFilterChange({ technician: e.target.value })}
                                    className="filter-select"
                                >
                                    <option value="all">All Technicians</option>
                                    <option value={technician.id}>My Conversations</option>
                                </select>
                            )}

                            <select
                                value={activeFilters.dateRange}
                                onChange={(e) => handleFilterChange({ dateRange: e.target.value })}
                                className="filter-select"
                            >
                                <option value="1d">Today</option>
                                <option value="7d">Next 7 Days</option>
                                <option value="30d">Next 30 Days</option>
                                <option value="all">All Time</option>
                            </select>
                        </div>

                        <div className="actions">
                            <button
                                className="btn btn--secondary"
                                onClick={handleRefresh}
                                disabled={refreshing}
                            >
                                <FaSync className={refreshing ? 'rotating' : ''} />
                                Refresh
                            </button>

                            <button
                                className={`btn btn--outline ${soundEnabled ? 'btn--sound-on' : 'btn--sound-off'}`}
                                onClick={handleSoundToggle}
                                title={soundEnabled ? 'Sound notifications enabled' : 'Sound notifications disabled'}
                            >
                                {soundEnabled ? <FaVolumeUp /> : <FaVolumeMute />}
                                {soundEnabled ? 'Sound On' : 'Sound Off'}
                            </button>

                            {isAdmin && (
                                <button
                                    className="btn btn--outline"
                                    onClick={() => setShowTestModal(true)}
                                >
                                    <FaCog />
                                    Configuration
                                </button>
                            )}

                            <a href={`/persona${window.location.search}`} className="btn btn--outline">
                                <FaUserCog />
                                My Persona
                            </a>
                        </div>
                    </div>

                    <ConversationLanes
                        laneData={laneData}
                        activeStatusFilter={activeFilters.status}
                        onLanePageChange={handleLanePageChange}
                        onConversationUpdate={handleConversationUpdate}
                        onCreateReminderFlow={handleCreateReminderFlow}
                        creatingReminderFlowConversationId={creatingReminderFlowConversationId}
                        onMessageSent={loadAllLaneData}
                        technician={technician}
                        isAdmin={isAdmin}
                        isManager={isManager}
                    />
                </div>
            </div>

            <ConfigurationModal
                isOpen={showTestModal}
                onClose={() => setShowTestModal(false)}
            />
        </div>
    );
};

export default Dashboard;
